import clsx from 'clsx';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { ModalProps } from './Modal.types';

const TAB_KEY = 'Tab';
const ESCAPE_KEY = 'Escape';
const FOCUSABLE_ELEMENTS =
  'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';

export const Modal = ({
  id,
  size,
  description,
  header,
  footer,
  footerAlign = 'left',
  isAnimated = false,
  children,
}: ModalProps): JSX.Element => {
  const ref = useRef();
  const toggleButton = useRef(null);
  const [focusableElements, setFocusableElements] = useState(null);
  const [isTabbing, setIsTabbing] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const useSetElements = (
    id: string, // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: any,
    description: ReactNode,
    header: ReactNode,
    footer: ReactNode,
    children: ReactNode
  ) => {
    useEffect(() => {
      toggleButton.current = document.getElementById(id);

      if (ref.current) {
        const focusableEls = ref.current.querySelectorAll(FOCUSABLE_ELEMENTS);
        setFocusableElements(focusableEls);
      }
    }, [id, ref, description, header, footer, children]);
  };

  useSetElements(id, ref, description, header, footer, children);

  const show = () => {
    setModalOpen(true);
    document.body.classList.add('is-modal-open');
    isTabbing && setTimeout(() => focusableElements[0].focus());
  };
  const hiding = () => {
    document.body.classList.remove('is-modal-open');
    document.body.classList.add('is-modal-closing');
    setTimeout(() => {
      document.body.classList.remove('is-modal-closing');
      setModalOpen(false);
      isTabbing && toggleButton.current.focus();
    }, 400);
  };
  const hide = () => {
    document.body.classList.remove('is-modal-open');
    setModalOpen(false);
    isTabbing && toggleButton.current.focus();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const useOnClickOutside = (ref: any, handler: any) => {
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const listener = (event: any) => {
        setIsTabbing(false);

        if (event.target == ref?.current) {
          isAnimated ? hiding() : hide();
          return;
        }

        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener('click', listener);
      return () => {
        document.removeEventListener('click', listener);
      };
    }, [ref, handler]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useOnClickOutside(ref, (event: any) => {
    event.target.id == id ? show() : null;
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const useFocusTrap = (ref: any, isModalOpen: boolean) => {
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const listener = (event: any) => {
        setIsTabbing(true);

        if (isModalOpen && event.key === ESCAPE_KEY) {
          isAnimated ? hiding() : hide();
          toggleButton.current.focus();
        } else if (!isModalOpen || event.key !== TAB_KEY) {
          return;
        } else if (
          event.shiftKey &&
          document.activeElement === focusableElements[0]
        ) {
          // shift + tab and is first element
          event.preventDefault();
          focusableElements[focusableElements.length - 1].focus();
        } else if (
          (!event.shiftKey &&
            document.activeElement ===
              focusableElements[focusableElements.length - 1]) ||
          !ref.current.contains(event.target)
        ) {
          // tab and is last element or target is not a child of the modal
          event.preventDefault();
          focusableElements[0].focus();
        }
      };

      document.addEventListener('keydown', listener);

      return () => {
        document.removeEventListener('keydown', listener);
      };
    }, [ref, isModalOpen]);
  };

  useFocusTrap(ref, isModalOpen);

  return (
    <div
      ref={ref}
      className={clsx('modal', 'modal_centered', {
        modal_small: size == 'Small',
        'modal_full-screen': size == 'Full',
        modal_animated: isAnimated,
      })}
      role='dialog'
      aria-modal='true'
      aria-hidden='false'
      style={isModalOpen ? { display: 'block' } : { display: 'none' }}
      data-testid='modal-trigger'
    >
      <div
        className='modal__container modal__container_scrollable'
        role='document'
      >
        {description && <div className='sr-only'>{description}</div>}
        <div className='modal__content'>
          <div className='modal__eyebrow'>
            <Button
              aria-label='Close'
              onClick={isAnimated ? hiding : hide}
              className='button button-close button-close_round button-close_large modal__close-button'
              testId='modal-close-btn'
            >
              <Icon icon='CloseX24' color='dark' size='large' />
            </Button>
          </div>

          {header && (
            <div className='modal__header' data-testid='modal-trigger-inner'>
              <h2 className='modal__title' id='modal_1-title'>
                {header}
              </h2>
            </div>
          )}

          <div className='modal__body'>{children}</div>

          {footer && (
            <>
              <div className='modal__divider divider' />
              <div
                className={clsx('modal__actions', {
                  'modal__actions-right': footerAlign === 'right',
                })}
              >
                {footer}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
